import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOMeta from '@/components/base/SEOMeta';
import Breadcrumbs from '@/components/base/Breadcrumbs';
import { JsonLD } from '@/components/base/SchemaOrg';
import type { WebPageSchema, OrganizationSchema, FAQPageSchema } from '@/components/base/SchemaOrg';


export default function About() {
  const { t } = useTranslation();

  const orgSchema: OrganizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '校花文化网',
    url: 'https://xiaohua-culture.com',
    description: t('site.description'),
    contactPoint: { '@type': 'ContactPoint', email: t('footer.email'), telephone: t('footer.phone'), contactType: 'customer service' },
  };
  const webPageSchema: WebPageSchema = {
    '@context': 'https://schema.org', '@type': 'WebPage', name: t('about.title'), url: 'https://xiaohua-culture.com/about', description: t('about.introText'), isPartOf: { '@type': 'WebSite', name: '校花文化网', url: 'https://xiaohua-culture.com' },
  };
  const faqSchema: FAQPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      { '@type': 'Question', name: '什么是校花评选？', acceptedAnswer: { '@type': 'Answer', text: '校花评选是高校校园文化活动的一种形式，旨在选拔德才兼备、全面发展的优秀女生代表，展现当代女大学生的精神风貌。' } },
      { '@type': 'Question', name: '校花评选的标准是什么？', acceptedAnswer: { '@type': 'Answer', text: '大多数高校采用综合评分体系，包括学业成绩（约20%）、才艺特长（约25%）、公益实践（约20%）、综合素质面试（约20%）和人气投票（约15%）。' } },
      { '@type': 'Question', name: '如何参与校花评选活动？', acceptedAnswer: { '@type': 'Answer', text: '关注学校学生会通知，按照报名要求提交材料。一般要求在校全日制本科生或研究生，无违纪记录，并具备一定才艺特长。' } },
    ],
  };

  const breadcrumbItems = [{ label: t('about.title') }];

  return (
    <>
      <SEOMeta title={t('about.title')} description={t('about.introText')} keywords="关于校花文化网,校园文化平台,校花资讯,联系我们" canonicalUrl="https://xiaohua-culture.com/about" />
      <JsonLD data={orgSchema as unknown as Record<string, unknown>} />
      <JsonLD data={webPageSchema as unknown as Record<string, unknown>} />
      <JsonLD data={faqSchema as unknown as Record<string, unknown>} />
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <Breadcrumbs items={breadcrumbItems} />
          </div>
        </div>

        <section className="py-14 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">{t('about.title')}</h1>
                <h2 className="text-lg md:text-xl font-heading text-foreground-700 mb-6">{t('about.intro')}</h2>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                  {t('about.introText')}
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                  自平台成立以来，我们已累计发布超过200篇高质量的校园文化内容，涵盖校花评选规则解读、校花人物专访、大学校园风光展示、青春故事分享等多个板块。我们的读者遍布全国各地高校，每月活跃读者超过50万人次。
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  我们相信，每一个校园都有自己独特的美，每一位学子都有自己的青春故事。校花文化网致力于成为连接校园与社会的桥梁，让更多人了解和感受中国高校的青春魅力。
                </p>
              </div>
              <div className="relative h-72 md:h-80 rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Beautiful%20Chinese%20university%20campus%20landscape%20with%20historic%20buildings%20and%20gardens%2C%20warm%20sunlight%2C%20serene%20atmosphere%2C%20educational%20and%20cultural%20setting%2C%20clean%20composition&width=800&height=600&seq=about-campus-culture&orientation=landscape"
                  alt={t('about.title')}
                  className="w-full h-full object-cover object-top rounded-lg"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">{t('about.reputation')}</h2>
              <p className="text-sm text-foreground-600">来自读者和合作伙伴的真实评价</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { text: t('about.reputationText1'), author: '李明', role: '北大校友 / 互联网从业者' },
                { text: t('about.reputationText2'), author: '王小雨', role: '大三学生 / 校园记者' },
                { text: t('about.reputationText3'), author: '赵教授', role: '高校教师 / 文化研究者' },
              ].map((review, i) => (
                <div key={i} className="bg-background-50 rounded-lg border border-background-200/70 p-6">
                  <div className="flex items-center gap-1 mb-4 text-accent-500">
                    {Array.from({ length: 5 }).map((_, j) => (
                      <i key={j} className="ri-star-fill text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    &ldquo;{review.text}&rdquo;
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-accent-100 flex items-center justify-center">
                      <span className="text-xs font-medium text-accent-700">{review.author[0]}</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{review.author}</p>
                      <p className="text-xs text-foreground-500">{review.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4">{t('about.contact')}</h2>
                <p className="text-sm text-foreground-600 leading-relaxed mb-6">{t('about.contactIntro')}</p>
                <div className="flex flex-col gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">邮箱</p>
                      <p className="text-sm font-medium text-foreground-900">{t('footer.email')}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                      <i className="ri-phone-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">电话</p>
                      <p className="text-sm font-medium text-foreground-900">{t('footer.phone')}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600">
                      <i className="ri-map-pin-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">地址</p>
                      <p className="text-sm font-medium text-foreground-900">{t('footer.address')}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-background-100 rounded-lg border border-background-200/70 p-6">
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-4">发送消息</h3>
                <form
                  data-readdy-form
                  onSubmit={(e) => {
                    e.preventDefault();
                    const form = e.currentTarget as HTMLFormElement;
                    const fd = new FormData(form);
                    const honeypot = (fd.get('phone_alt') as string || '').trim();
                    if (honeypot) {
                      const successEl = document.getElementById('contact-form-success');
                      if (successEl) successEl.style.display = 'block';
                      form.reset();
                      return;
                    }
                    fetch('https://readdy.ai/api/form/d9qa2h7nahb7382ov8i0', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                      body: new URLSearchParams(Object.fromEntries(fd.entries().filter(([k]) => k !== 'phone_alt')) as Record<string, string>).toString(),
                    }).then(async (r) => {
                      const text = await r.text();
                      if (r.ok) {
                        const successEl = document.getElementById('contact-form-success');
                        const errorEl = document.getElementById('contact-form-error');
                        if (successEl) successEl.style.display = 'block';
                        if (errorEl) errorEl.style.display = 'none';
                        form.reset();
                      } else {
                        const errorEl = document.getElementById('contact-form-error');
                        if (errorEl) { errorEl.style.display = 'block'; errorEl.textContent = '发送失败，请稍后重试'; }
                      }
                    }).catch(() => {
                      const errorEl = document.getElementById('contact-form-error');
                      if (errorEl) { errorEl.style.display = 'block'; errorEl.textContent = '网络错误，请稍后重试'; }
                    });
                  }}
                >
                  <div className="flex flex-col gap-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-800 mb-1.5">姓名</label>
                      <input id="contact-name" name="name" type="text" required className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-colors" placeholder="请输入您的姓名" />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-800 mb-1.5">邮箱</label>
                      <input id="contact-email" name="email" type="email" required className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-colors" placeholder="请输入您的邮箱" />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-800 mb-1.5">留言内容</label>
                      <textarea id="contact-message" name="message" rows={4} maxLength={500} required className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-colors resize-none" placeholder="请输入您的留言（500字以内）"></textarea>
                    </div>
                    <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="honeypot-field" />
                    <button type="submit" className="w-full px-6 py-2.5 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap">
                      发送消息
                    </button>
                    <div id="contact-form-success" className="hidden text-sm text-green-600 bg-green-50 px-4 py-2 rounded-lg">消息已发送，我们会尽快回复！</div>
                    <div id="contact-form-error" className="hidden text-sm text-accent-600 bg-accent-50 px-4 py-2 rounded-lg"></div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}